import { GLOBAL_DEFAULT_THUMBNAIL_URL } from '@/lib/tenant-media-defaults'

import { DEFAULT_SEO_IMAGE } from '@/configs/constants.config'
import { NON_FSDS_DEFAULT_LOGO_URL } from '@/configs/non-fsds-config'
import { siteConfig } from '@/constant/config'
import { normalizeHostname } from '@/utils/domain-branding-resolver'

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function buildSiteUrl(hostname?: string): string {
  const normalizedHostname = normalizeHostname(hostname)
  return normalizedHostname ? `https://${normalizedHostname}` : siteConfig.url
}

/** Hostname shown as the domain line in Messenger, Instagram, and iOS link previews. */
export function getSocialShareDomain(host?: string | null): string {
  return normalizeHostname(host)
}

/**
 * Optimizes title for social media sharing
 * @param title - The original title
 * @returns Optimized title for social media
 */
export const optimizeTitleForSocial = (title: string, brandName = 'FSDS'): string => {
  const cleanTitle = getCleanTitle(title, brandName)
  const normalizedBrandName = brandName.trim()

  if (!normalizedBrandName) {
    return cleanTitle
  }

  const brandedSuffix = `| ${normalizedBrandName}`
  return cleanTitle.endsWith(brandedSuffix) ? cleanTitle : `${cleanTitle} ${brandedSuffix}`
}

/**
 * Gets clean title without branding for image generation
 * @param title - The original title
 * @returns Clean title without branding
 */
export const getCleanTitle = (title: string, brandName = 'FSDS'): string => {
  const normalizedBrandName = brandName.trim()

  if (!normalizedBrandName) {
    return title.trim()
  }

  let result = title.trim()
  const legacyTenantSuffix = new RegExp(`\\s*\\|\\s*${escapeRegex(normalizedBrandName)}\\s+Tenant\\s*$`, 'i')
  const brandingPattern = new RegExp(`\\s*\\|\\s*${escapeRegex(normalizedBrandName)}\\s*$`, 'i')
  result = result.replace(legacyTenantSuffix, '').replace(brandingPattern, '').trim()
  return result
}

export function resolveSeoOgImageUrl(
  ogImage: string | undefined,
  options: {
    isFsdsDomain?: boolean | null
    defaultThumbnailUrl?: string
  }
): string {
  const trimmed = ogImage?.trim()
  const isGlobalDefault =
    !trimmed || trimmed === DEFAULT_SEO_IMAGE || trimmed === GLOBAL_DEFAULT_THUMBNAIL_URL

  if (options.isFsdsDomain !== true) {
    if (trimmed && !isGlobalDefault) {
      return trimmed
    }
    const tenantThumbnail = options.defaultThumbnailUrl?.trim()
    if (tenantThumbnail) {
      return tenantThumbnail
    }
    return NON_FSDS_DEFAULT_LOGO_URL
  }

  if (trimmed && !isGlobalDefault) {
    return trimmed
  }

  return DEFAULT_SEO_IMAGE
}

/**
 * Determines the content type based on the current pathname
 * @param pathname - The current pathname
 * @returns The content type for OG image generation
 */
export const getContentTypeFromPath = (
  pathname: string
): 'blog' | 'meme' | 'webinar' | 'topic' | 'course' | 'profile' => {
  if (pathname.includes('/meme')) return 'meme'
  if (pathname.includes('/webinars/')) return 'webinar'
  if (pathname.includes('/topic/') || pathname.includes('/forum/')) return 'topic'
  if (pathname.includes('/courses/')) return 'course'
  if (pathname.includes('/blogs/')) return 'blog'
  return 'blog'
}

/**
 * Strips HTML tags from content for meta descriptions
 * @param html - HTML content to strip
 * @returns Clean text without HTML tags
 */
export const stripHtml = (html: string): string => {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Truncates text for meta descriptions
 * @param text - Text to truncate
 * @param maxLength - Maximum length (default: 160)
 * @returns Truncated text with ellipsis if needed
 */
export const truncateText = (text: string, maxLength = 160): string => {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength - 3) + '...'
}

/**
 * Ensures a URL is absolute
 * @param path - URL to make absolute
 * @returns Absolute URL
 */
export const getAbsoluteUrl = (path?: string, hostname?: string): string => {
  if (!path) return ''
  if (path.startsWith('http')) return path
  return `${buildSiteUrl(hostname)}${path}`
}

/**
 * Validates and sanitizes metadata for SEO
 * @param metadata - Raw metadata object
 * @returns Validated and sanitized metadata
 */
export const validateSeoMetadata = (
  metadata: {
    title?: string
    description?: string
    author?: string
    keywords?: string
  },
  brandingDefaults?: {
    defaultTitle?: string
    defaultDescription?: string
    defaultAuthor?: string
    defaultKeywords?: string
  }
) => {
  const { title, description, author, keywords } = metadata

  const validatedTitle =
    title && title.trim()
      ? title.trim()
      : brandingDefaults?.defaultTitle || siteConfig.seo?.defaultTitle || 'FSDS - Fullstack Data Science'

  const validatedDescription =
    description && description.trim()
      ? truncateText(stripHtml(description.trim()), 155)
      : brandingDefaults?.defaultDescription ||
        siteConfig.seo?.defaultDescription ||
        'Khám phá các khóa học toàn diện về Full Stack Data Science'

  const validatedAuthor =
    author && author.trim()
      ? author.trim()
      : brandingDefaults?.defaultAuthor || siteConfig.seo?.defaultAuthor || 'FSDS Team'

  const validatedKeywords =
    keywords && keywords.trim()
      ? keywords.trim()
      : brandingDefaults?.defaultKeywords ||
        siteConfig.seo?.defaultKeywords ||
        'data science, machine learning, fullstack, FSDS, AI'

  return {
    title: validatedTitle,
    description: validatedDescription,
    author: validatedAuthor,
    keywords: validatedKeywords,
  }
}

/**
 * Debug function to log SEO metadata for development
 * @param metadata - Metadata to log
 * @param context - Context where this is being called from
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const debugSeoMetadata = (metadata: any, context: string) => {
  // eslint-disable-next-line no-console
  console.log(`[SEO Debug - ${context}]:`, {
    title: metadata.title,
    description: metadata.description?.substring(0, 100) + '...',
    author: metadata.author,
    ogImage: metadata.ogImage,
    url: metadata.canonicalUrl,
  })
}
